<script setup lang="ts">
import { computed } from "vue";
import {
  defaultChapterOverride,
  chapterNamingMode,
  chapterOverrideForNaming,
} from "../../lib/chapterNumbering";
import { isJust, just, nothing } from "../../lib/maybe";
import { useEditorSessionStore } from "../../stores/useEditorSessionStore";
import { useProjectWorkspaceStore } from "../../stores/useProjectWorkspaceStore";
import ZwMenu from "../ui/ZwMenu.vue";
import ZwMenuItem from "../ui/ZwMenuItem.vue";
const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const info = computed(() => session.activeSceneInfo);
const override = computed(() =>
  isJust(info.value) && isJust(session.projectConfig)
    ? (session.projectConfig.value.chapterNumberingOverrides[
        info.value.value.folder
      ] ?? defaultChapterOverride())
    : defaultChapterOverride(),
);
const naming = computed({
  get: () => chapterNamingMode(override.value),
  set: (value: string) => {
    if (
      isJust(info.value) &&
      (value === "default" || value === "custom" || value === "unnumbered")
    )
      void workspace.onUpdateChapterNumbering(
        info.value.value.folder,
        chapterOverrideForNaming(value),
      );
  },
});
const namingOptions = [
  { value: "default", label: "Default chapter name" },
  { value: "custom", label: "Custom chapter name" },
  { value: "unnumbered", label: "Unnumbered chapter" },
] as const;
</script>
<template>
  <ZwMenu
    v-if="isJust(info)"
    :key="info.value.folder"
    align="end"
    :menu-label="just('Chapter naming')"
  >
    <template #trigger="{ toggle, attrs }">
      <button
        type="button"
        class="chapter-options"
        aria-label="Chapter naming options"
        title="Chapter naming options"
        v-bind="attrs"
        @click="toggle()"
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="currentColor"
          aria-hidden="true"
        >
          <circle cx="5" cy="12" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="19" cy="12" r="1.5" />
        </svg>
      </button>
    </template>
    <ZwMenuItem
      v-for="option in namingOptions"
      :key="option.value"
      :label="option.label"
      :aria-label="option.label"
      :title="nothing"
      :shortcut="naming === option.value ? just('✓') : nothing"
      role="menuitemradio"
      :aria-checked="naming === option.value"
      @select="naming = option.value"
    />
  </ZwMenu>
</template>
<style scoped>
.chapter-options {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}
.chapter-options:hover {
  background: var(--hover-fill);
  color: var(--text);
}
.chapter-options:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
@media (pointer: coarse) {
  .chapter-options {
    width: 44px;
    height: 44px;
  }
}
</style>
